<template>
  <div id="life_facilities">
    <usc-hero :title="this.hero.title" :loResSrc="this.hero.loResSrc" :hiResSrc="this.hero.hiResSrc" :color="this.page.color"></usc-hero>
    <usc-intro>
      <p slot="p"><i>At Marshall, you’ll benefit from the world-class facilities of a renowned international research university — from our state-of-the art, dedicated hub for undergraduate business studies to outstanding libraries, recreational venues and dining options. Here are just a few of the amenities you will enjoy as a Marshall student.</i></p>
    </usc-intro>

    <!-- <usc-videoEmbed :frameTitle="this.video.frameTitle" :caption="this.video.caption" :videoSource="this.video.src"></usc-videoEmbed> -->

    <div class="body mb gutter-lg">
      <p><strong>Jill and Frank Fertitta Hall</strong><br />This five-story, 102,000-square-foot building is home base for Marshall undergraduates. It fosters a sense of community while featuring key services, sophisticated technology, plentiful nooks and crannies for relaxation or study, a dedicated undergrad business library and an Asian-inspired food court with a spacious outdoor courtyard.</p>
      <p>Fertitta Hall also houses the Lloyd Greif Center for Entrepreneurial Studies and the Experiential Learning Center as well as undergraduate admission, student services and student advising. The building’s two large lecture halls accommodate all undergraduates for presentations by guest speakers and business leaders hosted by the school. Students can reserve one of the building’s 50 breakout rooms for small-group collaboration or meetings. Smart classrooms are equipped with webcams and video playback capacity—and even skateboard racks.</p>
      <p><strong>7 Secrets of Fertitta Hall</strong></p>
      <div class="youtube-embed">
        <a href="https://www.youtube.com/watch?v=xM47js_r98A" 
          target="_blank" 
          rel="noopener" 
          aria-label="Watch 7 Secrets of Fertitta Hall on YouTube (opens in a new tab)">
          <img src="../../assets/7-Secrets-of-Fertitta-Hall-at-USC-Marshall-thumbnail.jpg" 
                alt="" 
                role="presentation">
        </a>
      </div>
    </div>
    <div class="body mb gutter-lg">
      <p><strong>USC Village</strong><br />At 15 acres, USC Village is the largest development in the history of South Los Angeles. In addition to student housing, it includes a wide range of restaurants and retailers. Here you’ll find cocktail and dining options ranging from Korean BBQ, burgers and authentic Italian cuisine to fresh Mexican food, sandwich shops and juice bars. There’s even a late-night cookie shop. </p>
      <p>Major retailers include Amazon and Target, but you’ll also discover trendy boutiques and a bike shop. Available service providers are also extensive, including Trader Joe’s grocery, CVS Pharmacy, USC Roski Eye Institute, shoe and computer repair shops, hair and nail salons, banking, FedEx and a yoga studio. The Village Fitness Center also offers a spacious lobby including a TV wall with lounge seating.</p>
      <p><strong>Video tour of USC Village</strong></p>
      <div class="youtube-embed">
        <a href="https://www.youtube.com/watch?v=eKMp-4Mmqdw" 
          target="_blank" 
          rel="noopener" 
          aria-label="Watch Video tour of USC Village on YouTube (opens in a new tab)">
          <img src="../../assets/USC-Village_thumbnail.jpg" 
                alt="" 
                role="presentation">
        </a>
      </div>
    </div>

    <div class="body gutter-lg mb">
      <p><strong>USC Libraries</strong><br />The <a href="https://libraries.usc.edu/" class="inline-hyperlink" target="_blank">USC Libraries</a> actively support the discovery, creation and preservation of knowledge. The staff develops collections and services that bolster and encourage the academic endeavors of faculty, students and staff; build a community of critical consumers of information; and help develop engaged world citizens. USC maintains 22 libraries and information centers and the <a href="https://digitallibrary.usc.edu/" class="inline-hyperlink" target="_blank">USC Digital Library</a>. </p>
      <p>Marshall features by two specialized libraries: the <a href="https://libraries.usc.edu/locations/gaughan-tiberti-library" class="inline-hyperlink" target="_blank">Gaughan & Tiberti Business Library</a> in Fertitta Hall, which collects information on a broad range of business topics that support Marshall courses and research, and the <a href="https://libraries.usc.edu/locations/accounting-library" class="inline-hyperlink" target="_blank">Accounting Library</a>, which is devoted to accounting and taxation materials.</p>
      <p>Other campus libraries popular with Marshall students include the beautiful and historic Doheny Memorial Library and Leavey Library, with resources that include an Information Commons (computing facilities and collaborative workrooms), Multimedia Commons (equipment and software for graphics, audio and video projects), Learning Rooms and Video Conference Rooms. Leavey is open 24 hours a day during most weeks when classes are in session or exams are underway (except on Sundays, when the library is closed from midnight to 9 a.m.).</p>
      <p><strong>USC Computing Centers</strong><br />In addition to the technology services offered at USC Libraries, USC Computing Centers are available in convenient campus locations. You can even check out a laptop on loan. Learn more about <a href="https://itservices.usc.edu/spaces/computingcenters/" class="inline-hyperlink" target="_blank">USC Computing Centers</a>.</p>
      <p><strong>USC Bookstore</strong><br />The campus bookstore is adjacent to the Student Union. In addition to textbooks, the store carries school supplies, other essentials, art supplies, computers and tech accessories, sports equipment, home décor, greeting cards, gifts and plenty of USC-branded clothing and gear. Browse the selection or shop online at <a href="https://itservices.usc.edu/spaces/computingcenters/" class="inline-hyperlink" target="_blank">USC Bookstores</a>.</p>
      <p><strong>Athletic Facilities</strong><br />The Lyon Recreation Center provides a wide array of programming and services, various specialized fitness areas, indoor athletic courts, and cardio and strength equipment. Recreational opportunities include club sport teams, the Uytengsu Aquatics Center, a weight-training center, racquetball and squash courts, and a cardiovascular center equipped with stationary bicycles, stair climbers, rowing machines and cross-country ski machines. The Lyon Center also offers locker rentals on a semester basis, which include towel service as well as rental of recreational equipment. Certain athletic clubs and classes charge a nominal fee.</p>
      <p>The USC Village Fitness Center offers three state-of-the-art fitness studios, a weight room featuring Hammer Strength benches and racks, and an extensive selection of ellipticals, bikes and treadmills.</p>
      <p><strong>Dining Facilities</strong><br /><a href="https://hospitality.usc.edu/" class="inline-hyperlink" target="_blank">USC Hospitality</a> provides a wide range of dining options at venues located throughout and near campus. You can select a meal plan for cost savings (even if you are not living in USC-owned-and-operated housing), or choose from a variety of locations using your USCard or a credit/debit card. Dining hours for USC facilities, which vary throughout the semester, are generally posted outside each location.</p>
      <p>The choices are extensive — from fast dining, takeaway meals and grocery items to gastropubs and full-service restaurants with farm-to-table cuisine. Whatever you’re craving is probably steps away — or can be delivered to you. A variety of vegan and vegetarian options and Kosher and Halal food are also available, and you can receive support from a registered dietitian for any special dietary needs.</p>
      <p><strong>Engemann Student Health Center</strong><br /><a href="https://studenthealth.usc.edu/" class="inline-hyperlink" target="_blank">The Engemann Student Health Center</a> is designed to meet all student healthcare needs. With a full range of medical services in primary and specialty care, counseling services and health promotion and disease prevention, your healthcare is covered. Conveniently located on the University Park Campus near the Lyon Center, the Engemann Student Health Center serves those students who are registered for classes and who have paid the Student Health Fee.</p>
      <p>The center strives to facilitate the completion of your academic career at USC by promoting healthy lifestyles and caring for your physical and psychological illnesses and concerns.</p>
    </div>


    <!-- <usc-videoEmbed :frameTitle="this.video2.frameTitle" :caption="this.video2.caption" :videoSource="this.video2.src"></usc-videoEmbed> -->

    <!-- <div class="gutter-md">
      <div class="usc-carousel__header">
        <div class="usc-carousel__title">USC Village</div>
        <div class="usc-carousel__nav">
          <div id="customize-controls" class="usc-carousel__controls">
            <button role="button" aria-label="Previous Image" @click="prevSlide" :disabled="prevDisabled" type="button" class="usc-carousel__button">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 6 13">
                <path fill="#231F20" d="M6 .5c0 .1 0 .2-.1.3L1.3 6.5l4.6 5.7c.1.3.1.6-.1.7-.3.1-.5.1-.7-.1L0 6.5 5.1.2c.2-.2.5-.2.7-.1.1.1.2.3.2.4z"/>
              </svg>
            </button>
            <button role="button" aria-label="Next Image" @click="nextSlide" :disabled="nextDisabled" type="button" class="usc-carousel__button">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 6 13">
                <path fill="#231F20" d="M0 12.5c0-.1 0-.2.1-.3l4.6-5.7L.1.8C0 .6 0 .3.2.1c.3-.1.5-.1.7.1L6 6.5.9 12.8c-.2.2-.5.3-.7.1-.1-.1-.2-.2-.2-.4z"/>
              </svg>
            </button>
          </div>
          <div class="usc-carousel__counter">
            {{this.slideIndex}} / {{this.totalSlides}}
          </div>
        </div>
      </div>
    </div>
    <div class="mb">
      <tiny-slider class="usc-carousel" :mouse-drag="false" :loop="false" items="1" :responsive="true" controlsContainer="#customize-controls">
        <div v-for="(slide, index) in this.slides" class="usc-carousel__slide">
          <div class="usc-carousel__img" :style="{ backgroundImage: 'url(' + slide.image + ')' }"></div>
          <div class="usc-carousel__outer">
            <img class="usc-carousel__inner" :src="slide.image" :alt="slide.alt || ''">
          </div>
        </div>
      </tiny-slider>
    </div> -->

  </div>
</template>

<script>
import hero from '@/components/Hero'
import intro from '@/components/Intro'
import Cta from '@/components/Cta'
import videoEmbed from '@/components/videoEmbed'
import VueTinySlider from 'vue-tiny-slider'

export default {
  name: 'Facilities',
  components: {
    'usc-hero': hero,
    'usc-intro': intro,
    'usc-videoEmbed': videoEmbed,
    'usc-cta': Cta,
    'tiny-slider': VueTinySlider
  },
  data () {
    return {
      page: {
        color: '#ffcc00'
      },
      hero: {
        title: 'Facilities',
        loResSrc: '../../../static/HeroImages/Marshall35.jpg',
        hiResSrc: '../../../static/HeroImages/Marshall35.jpg'
      },
      video: {
        caption: 'Now open: Fertitta Hall, an undergraduate business haven',
        src: 'https://player.vimeo.com/video/182418862',
        frameTitle: 'Video: Fertitta Hall'
      },
      video2: {
        caption: 'USC Village Aerial August 2017',
        src: 'https://www.youtube.com/embed/VSE2yI2IWRI',
        frameTitle: 'Video: USC Village'
      },
      slideIndex: 1,
      vueTinySlider: VueTinySlider,
      prevDisabled: true,
      nextDisabled: false,
      slides: [
        {
          image: '/static/Life_At_Marshall_Facilities_1.jpg',
          alt: 'USC Marshall',
          id: 0
        },
        {
          image: '/static/Life_At_Marshall_Facilities_2.jpg',
          alt: 'USC Village',
          id: 1
        },
        {
          image: '/static/Life_At_Marshall_Facilities_3.jpg',
          alt: 'USC Cafeteria',
          id: 2
        },
        {
          image: '/static/Life_At_Marshall_Facilities_4.jpg',
          alt: 'USC Swimming Pool',
          id: 3
        }
      ]
    }
  },
  created () {
    this.$root.setPageHeader('LIFE@MARSHALL', 'Facilities')
  },
  methods: {
    prevSlide () {
      if (this.slideIndex > 1) {
        this.slideIndex--
      } else {
        this.prevDisabled = true
      }
    },
    nextSlide () {
      if (this.slideIndex < this.totalSlides) {
        this.slideIndex++
      } else {
        this.nextDisabled = true
      }
    }
  },

  computed: {
    totalSlides () {
      return this.slides.length
    }
  }
}
</script>

<style scoped lang="scss">
@import '~tiny-slider/src/tiny-slider';
.youtube-embed {
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: 56.25%;
}
.youtube-embed img {
  width: 100%;
}
</style>



// WEBPACK FOOTER //
// src/pages/life-at-marshall/Facilities.vue